import * as React from "react";
import { useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import TuneIcon         from "@mui/icons-material/Tune";
import SpeedIcon        from "@mui/icons-material/Speed";
import PeopleIcon       from "@mui/icons-material/People";
import BarChartIcon     from "@mui/icons-material/BarChart";
import CampaignIcon     from "@mui/icons-material/Campaign";
import DescriptionIcon  from "@mui/icons-material/Description";
import InsightsIcon     from "@mui/icons-material/Insights";
import VerifiedIcon     from "@mui/icons-material/Verified";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";

/* ─── Canvas: animated chart / metrics background ─────────── */
function initChartCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  let rafId = null, t = 0, lastTs = 0, visible = false;
  const FPS_CAP = 1000 / 30;

  const resize = () => {
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  };
  resize();
  window.addEventListener("resize", resize, { passive: true });

  const io = new IntersectionObserver(([e]) => {
    visible = e.isIntersecting;
    if (visible && !rafId) rafId = requestAnimationFrame(animate);
  }, { threshold: 0 });
  io.observe(canvas);

  function wavePts(baseY, amp, freq, phase) {
    const pts = [], w = canvas.width, h = canvas.height;
    for (let x = 0; x <= w + 8; x += 8) {
      const nx = x / w;
      pts.push({ x, y: baseY + Math.sin(nx * freq + phase) * amp + Math.sin(nx * freq * 0.55 + phase * 1.5) * amp * 0.28 });
    }
    return pts;
  }

  function drawArea(pts, r, g, b, lineA, fillA) {
    if (pts.length < 2) return;
    const h = canvas.height, w = canvas.width;
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, `rgba(${r},${g},${b},${fillA})`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
    ctx.fillStyle = grad; ctx.fill();
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.strokeStyle = `rgba(${r},${g},${b},${lineA})`;
    ctx.lineWidth = 1.5; ctx.stroke();
  }

  const floaters = [
    { text: "+12.5K",  nx: 0.06, ny: 0.17, phase: 0.0 },
    { text: "4.8% ER", nx: 0.30, ny: 0.11, phase: 1.8 },
    { text: "91/100",  nx: 0.57, ny: 0.19, phase: 0.9 },
    { text: "↑ 34%",   nx: 0.82, ny: 0.13, phase: 2.6 },
  ];
  const barH = [0.30, 0.45, 0.38, 0.58, 0.50, 0.72, 0.63, 0.82];

  const animate = (ts) => {
    if (!visible) { rafId = null; return; }
    rafId = requestAnimationFrame(animate);
    if (ts - lastTs < FPS_CAP) return;
    lastTs = ts;
    t += 0.010;

    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    ctx.lineWidth = 1;
    for (let i = 0; i <= 12; i++) {
      ctx.strokeStyle = "rgba(255,255,255,0.025)";
      ctx.beginPath(); ctx.moveTo((w / 12) * i, 0); ctx.lineTo((w / 12) * i, h); ctx.stroke();
    }
    for (let i = 0; i <= 6; i++) {
      ctx.strokeStyle = i % 2 === 0 ? "rgba(255,255,255,0.048)" : "rgba(255,255,255,0.02)";
      ctx.beginPath(); ctx.moveTo(0, (h / 6) * i); ctx.lineTo(w, (h / 6) * i); ctx.stroke();
    }

    const amp = h * 0.13;
    drawArea(wavePts(h * 0.72, amp * 0.7, 2.0 * Math.PI, t * 1.3 + 2), 29,  158, 117, 0.18, 0.06);
    drawArea(wavePts(h * 0.63, amp * 0.8, 2.6 * Math.PI, t * 1.0 + 1), 74,  144, 226, 0.18, 0.06);
    drawArea(wavePts(h * 0.55, amp,       3.0 * Math.PI, t * 0.8    ), 255, 107,  53, 0.30, 0.10);

    const pts1 = wavePts(h * 0.55, amp, 3.0 * Math.PI, t * 0.8);
    const step = Math.floor(pts1.length / 8);
    ctx.fillStyle = "rgba(255,107,53,0.55)";
    for (let i = step; i < pts1.length - 1; i += step) {
      ctx.beginPath(); ctx.arc(pts1[i].x, pts1[i].y, 2.5, 0, Math.PI * 2); ctx.fill();
    }

    const bW = Math.max(12, Math.round(w * 0.018));
    const bGap = Math.max(5, Math.round(w * 0.008));
    const startX = w - barH.length * (bW + bGap) - w * 0.025;
    barH.forEach((bh, i) => {
      const aH = bh * h * 0.33 * (0.92 + 0.08 * Math.sin(t + i * 0.5));
      const x = startX + i * (bW + bGap), y = h - aH - h * 0.04;
      const bg = ctx.createLinearGradient(0, y, 0, h);
      const alpha = 0.08 + (i / barH.length) * 0.20;
      bg.addColorStop(0, `rgba(255,107,53,${alpha})`); bg.addColorStop(1, "rgba(255,107,53,0.01)");
      ctx.fillStyle = bg; ctx.fillRect(x, y, bW, aH);
      ctx.fillStyle = `rgba(255,107,53,${alpha * 2})`; ctx.fillRect(x, y, bW, 2);
    });

    ctx.font = "bold 11px system-ui,sans-serif";
    floaters.forEach((f) => {
      ctx.globalAlpha = ((Math.sin(t * 0.45 + f.phase) + 1) / 2) * 0.22;
      ctx.fillStyle = "#FF6B35";
      ctx.fillText(f.text, w * f.nx, h * f.ny);
    });
    ctx.globalAlpha = 1;
  };

  rafId = requestAnimationFrame(animate);
  return () => {
    if (rafId) cancelAnimationFrame(rafId);
    io.disconnect();
    window.removeEventListener("resize", resize);
  };
}

/* ─── Intelligence pillars (the "why") ────────────────────── */
const pillars = [
  {
    Icon:    InsightsIcon,
    accent:  "#FF6B35",
    tag:     "Audience Quality",
    headline: "Know who you're reaching before you spend.",
    body:    "Every creator carries a real Audience Quality Score bot%, genuine engagement ratio, demographic breakdown, and follower growth trend. Stop paying for fake reach.",
    stat:    { v: "91%", l: "avg audience authenticity" },
  },
  {
    Icon:    VerifiedIcon,
    accent:  "#22C55E",
    tag:     "Verified Contacts",
    headline: "Direct access. No agencies. No DM lottery.",
    body:    "Phone numbers and email addresses actively validated not just scraped. Reach any creator in minutes, not days. No middlemen, no inflated rates.",
    stat:    { v: "95%", l: "contact reach rate" },
  },
  {
    Icon:    TrackChangesIcon,
    accent:  "#4A90E2",
    tag:     "Campaign Intelligence",
    headline: "From brief to board report all in one place.",
    body:    "Meesha tracks every deliverable, every post metric, and every rupee spent automatically. Brief your team, monitor live performance, and deliver polished ROI reports in one click.",
    stat:    { v: "10×", l: "faster than manual" },
  },
];

/* ─── Feature cards (the "what") ──────────────────────────── */
const features = [
  {
    Icon:  TuneIcon,
    coral: true,
    title: "Advanced search filters",
    desc:  "Filter 2M+ creators by niche, city, age, gender, follower range, and engagement rate. Find your exact match in under 60 seconds.",
  },
  {
    Icon:  SpeedIcon,
    coral: false,
    title: "One-stop dashboard",
    desc:  "Creator profiles, audience analytics, and verified contacts in a single view. No tab-switching, no spreadsheets, no guesswork.",
  },
  {
    Icon:  PeopleIcon,
    coral: true,
    title: "Creator CRM",
    desc:  "Track every creator relationship outreach status, past collabs, deal values and notes organised in one place.",
  },
  {
    Icon:  BarChartIcon,
    coral: false,
    title: "Audience analytics",
    desc:  "Real follower quality scores, demographics, and engagement authenticity so you only partner with creators whose audiences are genuinely real.",
  },
  {
    Icon:  CampaignIcon,
    coral: true,
    title: "Campaign manager",
    desc:  "Assign creators, track deliverables, and monitor live post performance without a single spreadsheet.",
  },
  {
    Icon:  DescriptionIcon,
    coral: false,
    title: "Campaign reports",
    desc:  "Auto pull post metrics and generate polished, client ready reports in one click. Impress clients. Save hours.",
  },
];

const featureDelays = ["", "reveal-delay-1", "reveal-delay-2", "reveal-delay-3", "reveal-delay-4", "reveal-delay-5"];

/* ─── Pillar card component ────────────────────────────────── */
function PillarCard({ Icon, accent, tag, headline, body, stat, delay }) {
  return (
    <Box
      className={`reveal ${delay}`}
      sx={{
        position:   "relative",
        overflow:   "hidden",
        height:     "100%",
        bgcolor:    "rgba(255,255,255,0.04)",
        border:     "1px solid rgba(255,255,255,0.08)",
        borderRadius: "20px",
        p:          "28px 24px",
        transition: "all 0.35s ease",
        "&:hover": {
          bgcolor:     "rgba(255,255,255,0.07)",
          borderColor: `${accent}44`,
          transform:   "translateY(-6px)",
          boxShadow:   `0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px ${accent}22`,
        },
      }}
    >
      {/* Corner glow */}
      <Box sx={{
        position:     "absolute",
        top:          -40,
        right:        -40,
        width:        120,
        height:       120,
        borderRadius: "50%",
        background:   `radial-gradient(circle, ${accent}18, transparent 70%)`,
        pointerEvents: "none",
      }} />

      {/* Icon */}
      <Box sx={{
        width:          48,
        height:         48,
        borderRadius:   "14px",
        bgcolor:        `${accent}15`,
        border:         `1px solid ${accent}30`,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        mb:             2.5,
      }}>
        <Icon sx={{ color: accent, fontSize: 24 }} />
      </Box>

      {/* Tag */}
      <Typography sx={{
        fontSize:      "10px",
        fontWeight:    700,
        color:         accent,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        mb:            1.5,
      }}>
        {tag}
      </Typography>

      {/* Headline */}
      <Typography sx={{
        fontSize:   "17px",
        fontWeight: 700,
        color:      "white",
        lineHeight: 1.35,
        mb:         1.5,
      }}>
        {headline}
      </Typography>

      {/* Body */}
      <Typography sx={{
        fontSize:   "14px",
        color:      "rgba(255,255,255,0.48)",
        lineHeight: 1.72,
        mb:         3,
      }}>
        {body}
      </Typography>

      {/* Stat pill */}
      <Box sx={{
        display:      "inline-flex",
        alignItems:   "center",
        gap:          1,
        bgcolor:      `${accent}12`,
        border:       `1px solid ${accent}25`,
        borderRadius: "100px",
        px:           "14px",
        py:           "7px",
      }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 800, color: accent, lineHeight: 1 }}>
          {stat.v}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.45)" }}>
          {stat.l}
        </Typography>
      </Box>
    </Box>
  );
}

/* ─── Feature card component ───────────────────────────────── */
function FeatureCard({ Icon, coral, title, desc, delay }) {
  return (
    <Box
      className={`reveal ${delay}`}
      sx={{
        bgcolor:      "rgba(255,255,255,0.03)",
        border:       "1px solid rgba(255,255,255,0.07)",
        borderRadius: "16px",
        p:            "24px 20px",
        cursor:       "default",
        position:     "relative",
        overflow:     "hidden",
        transition:   "all 0.3s ease",
        "&:hover": {
          bgcolor:     "rgba(255,255,255,0.07)",
          borderColor: coral ? "rgba(255,107,53,0.35)" : "rgba(74,144,226,0.28)",
          transform:   "translateY(-5px)",
          boxShadow:   coral
            ? "0 16px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,107,53,0.12)"
            : "0 16px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(74,144,226,0.10)",
        },
      }}
    >
      <Box sx={{
        position:     "absolute",
        top:          -24,
        right:        -24,
        width:        72,
        height:       72,
        borderRadius: "50%",
        background:   coral
          ? "radial-gradient(circle, rgba(255,107,53,0.10), transparent 70%)"
          : "radial-gradient(circle, rgba(74,144,226,0.08), transparent 70%)",
        pointerEvents: "none",
      }} />

      <Box sx={{
        width:          40,
        height:         40,
        borderRadius:   "11px",
        bgcolor:        coral ? "rgba(255,107,53,0.14)" : "rgba(255,255,255,0.06)",
        border:         `1px solid ${coral ? "rgba(255,107,53,0.28)" : "rgba(255,255,255,0.09)"}`,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        mb:             "14px",
      }}>
        <Icon sx={{ color: coral ? "#FF6B35" : "rgba(255,255,255,0.60)", fontSize: 20 }} />
      </Box>

      <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "white", mb: "7px", lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.45)", lineHeight: 1.7 }}>
        {desc}
      </Typography>
    </Box>
  );
}

/* ─── Combined section ─────────────────────────────────────── */
function LandingWhyusSection() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    return initChartCanvas(canvasRef.current);
  }, []);

  return (
    <Box sx={{
      bgcolor:  "#0D1B3E",
      position: "relative",
      overflow: "hidden",
      py:       { xs: "60px", md: "100px" },
      px:       { xs: "20px", md: "80px" },
    }}>
      {/* ── Animated chart canvas ── */}
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", zIndex: 0 }}
      />

      {/* ── Ambient glows ── */}
      <Box sx={{
        position:     "absolute",
        width:        520,
        height:       520,
        top:          "-100px",
        right:        "5%",
        background:   "radial-gradient(circle, rgba(255,107,53,0.09) 0%, rgba(255,107,53,0.03) 45%, transparent 70%)",
        pointerEvents: "none",
        zIndex:       0,
      }} />
      <Box sx={{
        position:     "absolute",
        width:        400,
        height:       400,
        bottom:       "20px",
        left:         "3%",
        background:   "radial-gradient(circle, rgba(74,144,226,0.08) 0%, rgba(74,144,226,0.03) 45%, transparent 70%)",
        pointerEvents: "none",
        zIndex:       0,
      }} />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>

        {/* ── Section header ── */}
        <Box className="reveal" sx={{ textAlign: "center", mb: { xs: "48px", md: "68px" } }}>
          <Box component="span" sx={{
            display:       "inline-block",
            color:         "#FF6B35",
            bgcolor:       "rgba(255,107,53,0.1)",
            border:        "1px solid rgba(255,107,53,0.25)",
            px:            "14px",
            py:            "5px",
            borderRadius:  "100px",
            fontSize:      "10px",
            fontWeight:    700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb:            2.5,
          }}>
            Platform Intelligence
          </Box>

          <Typography component="h2" sx={{
            fontSize:   "clamp(28px, 3.8vw, 48px)",
            fontWeight: 800,
            color:      "white",
            lineHeight: 1.15,
            mb:         2,
            maxWidth:   "720px",
            mx:         "auto",
          }}>
            The intelligence layer{" "}
            <Box component="span" sx={{
              background:           "linear-gradient(90deg, #FF6B35 0%, #FF9F6B 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor:  "transparent",
              backgroundClip:       "text",
            }}>
              behind every decision.
            </Box>
          </Typography>

          <Typography sx={{
            fontSize:   "17px",
            color:      "rgba(255,255,255,0.45)",
            maxWidth:   "560px",
            mx:         "auto",
            lineHeight: 1.7,
          }}>
            Three data layers power every campaign call. Six tools replace everything else.
          </Typography>
        </Box>

        {/* ── Intelligence pillars (3 columns) ── */}
        <Box sx={{
          display:             "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap:                 "20px",
          mb:                  { xs: "48px", md: "64px" },
        }}>
          {pillars.map((p, i) => (
            <PillarCard key={i} {...p} delay={i === 0 ? "" : `reveal-delay-${i}`} />
          ))}
        </Box>

        {/* ── Visual divider ── */}
        <Box className="reveal" sx={{
          display:    "flex",
          alignItems: "center",
          gap:        3,
          mb:         { xs: "40px", md: "56px" },
        }}>
          <Box sx={{ flex: 1, height: "1px", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.10))" }} />
          <Box sx={{
            display:       "flex",
            alignItems:    "center",
            gap:           1.5,
            bgcolor:       "rgba(255,255,255,0.04)",
            border:        "1px solid rgba(255,255,255,0.08)",
            borderRadius:  "100px",
            px:            "18px",
            py:            "8px",
            flexShrink:    0,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#FF6B35" }} />
            <Typography sx={{
              fontSize:      "11px",
              fontWeight:    700,
              color:         "rgba(255,255,255,0.40)",
              textTransform: "uppercase",
              letterSpacing: "0.09em",
              whiteSpace:    "nowrap",
            }}>
              Everything you need to execute
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#4A90E2" }} />
          </Box>
          <Box sx={{ flex: 1, height: "1px", background: "linear-gradient(90deg, rgba(255,255,255,0.10), transparent)" }} />
        </Box>

        {/* ── Feature cards (3×2 grid) ── */}
        <Box sx={{
          display:             "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap:                 "16px",
        }}>
          {features.map((f, i) => (
            <FeatureCard key={i} {...f} delay={featureDelays[i]} />
          ))}
        </Box>

      </Container>
    </Box>
  );
}

export default LandingWhyusSection;
